import { Alert, Button, Layout, LayoutWidth, Loader, Text, Tooltip } from '@lifebit-ai/lds'
import React from 'react'
import { Link } from 'react-router-dom'

import RejectionModal from 'components/RejectionModal/RejectionModal'
import { LoadingWrapper, Title } from 'core/Common.styles'
import NotFound from 'modules/general/not-found/NotFound'
import { RequestActivityCard } from 'modules/requests/components/RequestActivity/RequestActivityCard'
import { RequestDetailLayout } from 'modules/requests/components/RequestDetailLayout/RequestDetailLayout'
import { RequestLabelsCard } from 'modules/requests/components/RequestLabelsCard/RequestLabelsCard'
import { RelatedDataAccessRequests } from 'modules/requests/data-access-requests/components/RelatedDataAccessRequests/RelatedDataAccessRequests'
import { getDataAccessRequestDetailsPath } from 'modules/requests/requestDetailPaths'
import { AlertContainer } from 'modules/resultsV2/Results.styles'
import ViewFormModal from 'modules/resultsV2/components/ViewFormModal/ViewFormModal'
import {
  APPROVAL_ALERT_TEST_ID,
  APPROVE_BUTTON_TEST_ID,
  REJECT_BUTTON_TEST_ID,
  REQUEST_ADDITIONAL_INFORMATION_BUTTON_TEST_ID
} from 'modules/resultsV2/utils'
import { DataAccessRequestAttachmentSubmission } from 'services/data-access-requests/enums'
import { toDataAccessRequestActivitySource } from 'services/request-activity/mappers'
import { isRelatedDarEnabled } from 'utils/env'

import { DataAccessRequestApprovalModal } from './DataAccessRequestApprovalModal'
import * as styles from './DataAccessRequestDetails.styles'
import { DataAccessRequestDetailsFormCard } from './DataAccessRequestDetailsFormCard'
import { DataAccessRequestDetailsTags } from './DataAccessRequestDetailsTags'
import { DataAccessRequestFailureAlert } from './DataAccessRequestFailureAlert'
import { DataAccessRequestParticipantFileCard } from './DataAccessRequestParticipantFileCard'
import { RequestAdditionalInformationModal } from './RequestAdditionalInformationModal'
import {
  getHasDataAccessRequestAdditionalInformationResponse,
  getIsDataAccessRequestReplica,
  getLinkedChildRequestId,
  getLinkedParentRequestId
} from './additionalInformationEligibility'
import {
  DATA_ACCESS_REQUEST_ADDITIONAL_INFORMATION_FORM_TITLE,
  DATA_ACCESS_REQUEST_ADDITIONAL_INFORMATION_FORM_VIEW_ACTION_TEST_ID,
  DATA_ACCESS_REQUEST_ADDITIONAL_INFORMATION_METADATA_TEST_ID,
  DATA_ACCESS_REQUEST_ADDITIONAL_INFORMATION_MODAL_OK_LABEL,
  DATA_ACCESS_REQUEST_ADDITIONAL_INFORMATION_MODAL_TITLE,
  DATA_ACCESS_REQUEST_APPROVAL_CONFIGURATION_ERROR_ALERT,
  DATA_ACCESS_REQUEST_APPROVAL_CONFIGURATION_ERROR_ALERT_TEST_ID,
  DATA_ACCESS_REQUEST_FORM_TITLE,
  DATA_ACCESS_REQUEST_LINKED_CHILD_ALERT_TEST_ID,
  DATA_ACCESS_REQUEST_ORIGINAL_FORM_TITLE
} from './constants'
import { useDataAccessRequestDetails } from './hooks/useDataAccessRequestDetails'

export const DATA_ACCESS_REQUEST_DETAILS_LOADER_TEST_ID = 'data-access-request-details-loader'
export const DATA_ACCESS_REQUEST_DETAILS_TEST_ID = 'data-access-request-details'
export const DATA_ACCESS_REQUEST_APPROVAL_ACTIONS_TEST_ID = 'data-access-request-approval-actions'

export const DataAccessRequestDetails = (): JSX.Element => {
  const {
    approvalModalDescription,
    approveRequest,
    approveRequestMutation,
    availableLabels,
    closeAdditionalInformationModal,
    closeApprovalModal,
    closeRejectionModal,
    closeViewFormModal,
    dataAccessRequest,
    parentDataAccessRequest,
    isLoadingParentDataAccessRequest,
    isParentDataAccessRequestError,
    goBack,
    isApprovalActionsVisible,
    isApproveActionVisible,
    isApprovalConfigurationErrorAlertVisible,
    approvalActionsDisabledReason,
    isApprovalModalOpen,
    autoSharingOnApprovalEnabled,
    isApprovalRestricted,
    isParticipantFileDownloadDisabled,
    isParticipantFileDownloadLoading,
    isFailureAlertVisible,
    isLoading,
    isNotFound,
    isAdditionalInformationModalOpen,
    isRejectionModalOpen,
    isRequestAdditionalInformationActionVisible,
    isViewFormModalOpen,
    onAddDataAccessRequestLabel,
    onAssignExistingDataAccessRequestLabel,
    onDeleteDataAccessRequestLabel,
    onValidateDataAccessRequestLabel,
    openApprovalModal,
    openAdditionalInformationModal,
    openRejectionModal,
    openViewFormModal,
    rejectRequest,
    rejectRequestMutation,
    rejectionModalDescription,
    requestAdditionalInformation,
    requestAdditionalInformationMutation,
    requestAdditionalInformationModalDescription,
    requestId,
    viewFormAttachmentSubmission,
    viewFormSubmission
  } = useDataAccessRequestDetails()

  if (isLoading) {
    return (
      // Same width as the loaded page, so the content does not jump when it arrives.
      <Layout layoutWidth={LayoutWidth.XLarge}>
        <styles.StyledContent>
          <LoadingWrapper data-testid={DATA_ACCESS_REQUEST_DETAILS_LOADER_TEST_ID}>
            <Loader />
          </LoadingWrapper>
        </styles.StyledContent>
      </Layout>
    )
  }

  if (isNotFound || !dataAccessRequest) {
    return <NotFound />
  }

  const childRequestId = getLinkedChildRequestId(dataAccessRequest)
  const isReplica = getIsDataAccessRequestReplica(dataAccessRequest)
  const linkedParentRequestId = getLinkedParentRequestId(dataAccessRequest)
  const hasAdditionalInformationSubmission = getHasDataAccessRequestAdditionalInformationResponse(dataAccessRequest)
  const isParentMetadataError =
    isParentDataAccessRequestError &&
    parentDataAccessRequest === undefined &&
    linkedParentRequestId !== undefined &&
    !isLoadingParentDataAccessRequest

  return (
    <Layout data-testid={DATA_ACCESS_REQUEST_DETAILS_TEST_ID} layoutWidth={LayoutWidth.XLarge}>
      <Layout.Header
        hasSeparator={false}
        onClickGoBack={goBack}
        subtitle={<DataAccessRequestDetailsTags dataAccessRequest={dataAccessRequest} />}
        title={<Title>Data access request</Title>}
        actions={
          isApprovalActionsVisible ? (
            <Tooltip title={approvalActionsDisabledReason || undefined}>
              <styles.ButtonsWrapper
                aria-label={approvalActionsDisabledReason || undefined}
                data-testid={DATA_ACCESS_REQUEST_APPROVAL_ACTIONS_TEST_ID}
              >
                <Button
                  data-testid={REJECT_BUTTON_TEST_ID}
                  type="secondary"
                  onClick={openRejectionModal}
                  loading={rejectRequestMutation.isPending}
                  disabled={!!approvalActionsDisabledReason}
                  danger
                >
                  Reject
                </Button>
                {isRequestAdditionalInformationActionVisible ? (
                  <Button
                    data-testid={REQUEST_ADDITIONAL_INFORMATION_BUTTON_TEST_ID}
                    type="secondary"
                    onClick={openAdditionalInformationModal}
                    disabled={!!approvalActionsDisabledReason}
                    loading={requestAdditionalInformationMutation.isPending}
                  >
                    Request additional information
                  </Button>
                ) : null}
                {isApproveActionVisible ? (
                  <Button
                    data-testid={APPROVE_BUTTON_TEST_ID}
                    type="primary"
                    onClick={openApprovalModal}
                    disabled={!!approvalActionsDisabledReason}
                  >
                    Approve
                  </Button>
                ) : null}
              </styles.ButtonsWrapper>
            </Tooltip>
          ) : undefined
        }
      />

      <Layout.Separator />

      <styles.StyledContent>
        <RequestDetailLayout
          aside={
            <>
              <RequestLabelsCard
                addedLabels={dataAccessRequest.labels}
                allLabels={availableLabels}
                onAddResultLabel={onAddDataAccessRequestLabel}
                onValidateResultLabel={onValidateDataAccessRequestLabel}
                onClickLabelMenuItem={onAssignExistingDataAccessRequestLabel}
                onDeleteResultLabel={onDeleteDataAccessRequestLabel}
              />
              <RequestActivityCard source={toDataAccessRequestActivitySource(dataAccessRequest)} />
            </>
          }
        >
          {isApprovalRestricted && (
            <AlertContainer>
              <Alert type="info" data-testid={APPROVAL_ALERT_TEST_ID}>
                This data access request is from a workspace with approval restrictions enabled. Since you submitted the
                request, you cannot approve or reject it. Another Airlock Manager will need to review it.
              </Alert>
            </AlertContainer>
          )}

          {isApprovalConfigurationErrorAlertVisible && (
            <AlertContainer>
              <Alert
                showIcon
                type="danger"
                data-testid={DATA_ACCESS_REQUEST_APPROVAL_CONFIGURATION_ERROR_ALERT_TEST_ID}
              >
                {DATA_ACCESS_REQUEST_APPROVAL_CONFIGURATION_ERROR_ALERT}
              </Alert>
            </AlertContainer>
          )}

          {isFailureAlertVisible && <DataAccessRequestFailureAlert dataAccessRequest={dataAccessRequest} />}

          {childRequestId ? (
            <AlertContainer>
              <Alert showIcon type="info" data-testid={DATA_ACCESS_REQUEST_LINKED_CHILD_ALERT_TEST_ID}>
                A linked request is pending review.{' '}
                <Link to={getDataAccessRequestDetailsPath(childRequestId)}>
                  <Text variant="small" weight="semibold" color="info">
                    View pending request
                  </Text>
                </Link>
              </Alert>
            </AlertContainer>
          ) : null}

          <styles.DetailsCards>
            <DataAccessRequestParticipantFileCard
              isDownloadDisabled={isParticipantFileDownloadDisabled}
              isDownloadLoading={isParticipantFileDownloadLoading}
              participantFile={dataAccessRequest.participantFile}
              requestId={dataAccessRequest._id}
            />
            {isReplica ? (
              <>
                {hasAdditionalInformationSubmission && dataAccessRequest.additionalInformationSubmission ? (
                  <DataAccessRequestDetailsFormCard
                    formSubmission={dataAccessRequest.additionalInformationSubmission}
                    metadataProps={{
                      dataAccessRequest,
                      metadataTestId: DATA_ACCESS_REQUEST_ADDITIONAL_INFORMATION_METADATA_TEST_ID,
                      parentRequestLinkId: linkedParentRequestId,
                      requestedAtOverride: dataAccessRequest.additionalInformationSubmission.submittedAt
                    }}
                    onViewForm={() =>
                      openViewFormModal(
                        dataAccessRequest.additionalInformationSubmission!,
                        DataAccessRequestAttachmentSubmission.Additional
                      )
                    }
                    title={DATA_ACCESS_REQUEST_ADDITIONAL_INFORMATION_FORM_TITLE}
                    viewActionTestId={DATA_ACCESS_REQUEST_ADDITIONAL_INFORMATION_FORM_VIEW_ACTION_TEST_ID}
                  />
                ) : null}
                {dataAccessRequest.formSubmission ? (
                  <DataAccessRequestDetailsFormCard
                    formSubmission={dataAccessRequest.formSubmission}
                    metadataProps={
                      parentDataAccessRequest !== undefined
                        ? {
                            dataAccessRequest: parentDataAccessRequest,
                            includeQuerySections: false,
                            showRejectionReason: true,
                            showSharingMetadata: false
                          }
                        : undefined
                    }
                    isMetadataError={isParentMetadataError}
                    isMetadataLoading={isLoadingParentDataAccessRequest && linkedParentRequestId !== undefined}
                    onViewForm={() =>
                      openViewFormModal(
                        dataAccessRequest.formSubmission!,
                        DataAccessRequestAttachmentSubmission.Original
                      )
                    }
                    title={DATA_ACCESS_REQUEST_ORIGINAL_FORM_TITLE}
                  />
                ) : null}
              </>
            ) : (
              <>
                {dataAccessRequest.formSubmission ? (
                  <DataAccessRequestDetailsFormCard
                    formSubmission={dataAccessRequest.formSubmission}
                    metadataProps={{ dataAccessRequest }}
                    onViewForm={() =>
                      openViewFormModal(
                        dataAccessRequest.formSubmission!,
                        DataAccessRequestAttachmentSubmission.Original
                      )
                    }
                    title={DATA_ACCESS_REQUEST_FORM_TITLE}
                  />
                ) : null}
                {hasAdditionalInformationSubmission && dataAccessRequest.additionalInformationSubmission ? (
                  <DataAccessRequestDetailsFormCard
                    formSubmission={dataAccessRequest.additionalInformationSubmission}
                    onViewForm={() =>
                      openViewFormModal(
                        dataAccessRequest.additionalInformationSubmission!,
                        DataAccessRequestAttachmentSubmission.Additional
                      )
                    }
                    title={DATA_ACCESS_REQUEST_ADDITIONAL_INFORMATION_FORM_TITLE}
                    viewActionTestId={DATA_ACCESS_REQUEST_ADDITIONAL_INFORMATION_FORM_VIEW_ACTION_TEST_ID}
                  />
                ) : null}
              </>
            )}
          </styles.DetailsCards>

          {isRelatedDarEnabled() && <RelatedDataAccessRequests dataAccessRequest={dataAccessRequest} />}
        </RequestDetailLayout>
      </styles.StyledContent>

      <ViewFormModal
        open={isViewFormModalOpen}
        onClose={closeViewFormModal}
        formSubmission={viewFormSubmission}
        resultId={dataAccessRequest._id}
        verificationAttachmentSignedUrlSource="data-access-request"
        attachmentSubmission={viewFormAttachmentSubmission}
      />
      <DataAccessRequestApprovalModal
        key={`${requestId ?? 'unknown'}-${isApprovalModalOpen ? 'open' : 'closed'}-${
          autoSharingOnApprovalEnabled ? 'auto-sharing' : 'manual'
        }`}
        autoSharingOnApprovalEnabled={autoSharingOnApprovalEnabled}
        description={approvalModalDescription}
        isError={approveRequestMutation.isError}
        isLoading={approveRequestMutation.isPending}
        isOpen={isApprovalModalOpen}
        requestId={requestId ?? ''}
        onApprove={approveRequest}
        onClose={closeApprovalModal}
      />
      <RejectionModal
        action={rejectRequest}
        description={rejectionModalDescription}
        isError={rejectRequestMutation.isError}
        isLoading={rejectRequestMutation.isPending}
        isOpen={isRejectionModalOpen}
        okTitle="Reject access"
        onClose={closeRejectionModal}
        title="Reject data access request?"
      />
      {isRequestAdditionalInformationActionVisible ? (
        <RequestAdditionalInformationModal
          action={requestAdditionalInformation}
          description={requestAdditionalInformationModalDescription}
          isError={requestAdditionalInformationMutation.isError}
          isLoading={requestAdditionalInformationMutation.isPending}
          isOpen={isAdditionalInformationModalOpen}
          okTitle={DATA_ACCESS_REQUEST_ADDITIONAL_INFORMATION_MODAL_OK_LABEL}
          onClose={closeAdditionalInformationModal}
          title={DATA_ACCESS_REQUEST_ADDITIONAL_INFORMATION_MODAL_TITLE}
        />
      ) : null}
    </Layout>
  )
}

export default DataAccessRequestDetails
